import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sparkles } from "lucide-react";
import Reveal from "@/components/site/Reveal";

const STORY = [
  "Welcome to Palace of Flavor, a family-owned catering company proudly serving the Greater Tampa Bay Area with authentic Indian and Pakistani cuisine. Founded by husband-and-wife team Chef Ayesha and Jafar Rizvi, Palace of Flavor was created from a shared passion for bringing people together through exceptional food, warm hospitality, and generations of culinary tradition.",
  "At the heart of Palace of Flavor is Chef Ayesha, an accomplished chef with over seven years of professional culinary experience specializing in authentic Indian and Pakistani cuisine. Inspired by the rich culinary heritage of Lucknow, India — renowned as the birthplace of the royal Awadhi style of cooking — she combines treasured family recipes with premium ingredients, handcrafted spice blends, and time-honored techniques.",
  "Alongside her husband, Jafar Rizvi, Chef Ayesha has built Palace of Flavor with a vision of sharing the authentic tastes of their heritage with families, businesses, and communities throughout Tampa Bay. Together, they believe great food is more than a meal — it's an experience that brings people together and creates lasting memories.",
  "From slow-cooked biryanis and flavorful curries to sizzling tandoori specialties, juicy kebabs, freshly baked naan, and handcrafted desserts, every menu is thoughtfully customized to suit your event and exceed your guests' expectations — from an intimate family dinner to a grand celebration.",
];

const SPECIALTIES = [
  "Authentic Indian & Pakistani Cuisine",
  "Traditional Awadhi (Lucknow) Recipes",
  "Halal Catering",
  "Weddings & Receptions",
  "Corporate Events",
  "Private Parties",
  "Eid & Cultural Celebrations",
  "Vegetarian & Vegan Options",
  "Buffet, Family-Style & Plated Service",
  "Customized Catering Menus",
];

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["10%", "-10%"]);

  return (
    <section id="about" ref={ref} className="relative py-24 md:py-40 bg-[#0A0607]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className="font-body text-xs tracking-[0.4em] uppercase text-[#D4AF37] mb-4">Our Story</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#FDFBF7] leading-[1.02] max-w-4xl">
            Where the royal flavors of <span className="gold-gradient-text italic">Lucknow</span> meet the heart of <span className="gold-gradient-text italic">Tampa Bay.</span>
          </h2>
        </Reveal>

        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-16 lg:gap-24 mt-16 md:mt-20 items-start">
          {/* Story */}
          <div className="space-y-6">
            {STORY.map((p, i) => (
              <Reveal key={i} delay={i * 0.06} y={24}>
                <p className={`font-body leading-relaxed ${i === 0 ? "text-[#FDFBF7] text-lg md:text-xl" : "text-[#A89F91] text-base md:text-lg"}`}>
                  {p}
                </p>
              </Reveal>
            ))}

            <Reveal delay={0.1}>
              <div className="mt-10 flex flex-wrap items-center gap-6 border-t border-[#D4AF37]/15 pt-8">
                <div>
                  <p className="font-display text-2xl text-[#D4AF37]">Chef Ayesha</p>
                  <p className="font-body text-xs tracking-[0.15em] uppercase text-[#A89F91]">Founder & Head Chef</p>
                </div>
                <span className="w-px h-10 bg-[#D4AF37]/20 hidden sm:block" />
                <div>
                  <p className="font-display text-2xl text-[#D4AF37]">Jafar Rizvi</p>
                  <p className="font-body text-xs tracking-[0.15em] uppercase text-[#A89F91]">Co-Founder</p>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Parallax images */}
          <div className="relative h-[440px] md:h-[620px] hidden lg:block">
            <motion.div style={{ y: y1 }} className="absolute top-0 right-0 w-[64%] h-[60%] overflow-hidden rounded-tl-[4rem] rounded-br-[4rem] border border-[#D4AF37]/20">
              <img src="https://images.unsplash.com/photo-1585937421612-70a008356fbe?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" alt="Authentic Indian curry" className="w-full h-full object-cover" />
            </motion.div>
            <motion.div style={{ y: y2 }} className="absolute bottom-0 left-0 w-[60%] h-[58%] overflow-hidden rounded-tr-[4rem] rounded-bl-[4rem] border border-[#D4AF37]/20 shadow-2xl">
              <img src="https://images.unsplash.com/photo-1617692855027-33b14f061079?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" alt="Tandoori specialties" className="w-full h-full object-cover" />
            </motion.div>
            <div className="absolute bottom-[16%] right-[4%] w-40 h-40 rounded-full border border-[#D4AF37]/30 flex items-center justify-center text-center backdrop-blur-sm bg-[#0A0607]/50">
              <span className="font-display text-[#D4AF37] leading-tight text-lg">Awadhi<br /><span className="text-2xl">Heritage</span></span>
            </div>
          </div>
        </div>

        {/* Specialties */}
        <Reveal>
          <div className="mt-24 md:mt-32">
            <p className="font-body text-xs tracking-[0.4em] uppercase text-[#D4AF37] mb-8 flex items-center gap-3">
              <Sparkles size={15} /> Our Specialties
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
              {SPECIALTIES.map((s, i) => (
                <Reveal key={s} delay={(i % 3) * 0.05} y={18}>
                  <div className="flex items-center gap-3 border-b border-[#D4AF37]/10 pb-4">
                    <span className="text-[#D4AF37] text-sm">◆</span>
                    <span className="font-body text-[#FDFBF7]/90">{s}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Tagline */}
        <Reveal>
          <div className="mt-24 md:mt-32 text-center">
            <p className="font-display italic text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#FDFBF7] max-w-4xl mx-auto">
              “From the royal kitchens of Lucknow <span className="gold-gradient-text">to your table.</span>”
            </p>
            <p className="font-body text-xs tracking-[0.4em] uppercase text-[#A89F91] mt-6">
              Palace of Flavor · Authentic Indian & Pakistani Cuisine
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
